import React, {
    forwardRef,
    useContext,
    useEffect,
    useImperativeHandle,
    useState,
} from 'react';

import mergeRefs from 'merge-refs';

import {DropdownMenu, DropdownMenuGroup, DropdownMenuItem} from '@pb/kit-component-dropdown-menu';
import {useArrowNavigationItem} from '@pb/kit-hook-use-arrow-navigation';
import {withCssModule} from '@pb/kit-util-with-css-module';

import {TabCaption} from './tab-caption.component';
import {TabsContext} from './tabs-context';
import styles from './tabs.component.module.styl';

import type {DropdownMenuItemProps} from '@pb/kit-component-dropdown-menu';
import type {
    ComponentProps,
    ReactNode,
} from 'react';


const classNames = withCssModule(styles);
const noop = () => undefined;

interface RenderAnchorProps {
    anchorRef: React.RefCallback<HTMLAnchorElement>,
    getAnchorProps: (props: {
        onClick: (event: React.MouseEvent<HTMLAnchorElement>) => void,
    }) => Record<string, unknown>,
    positionRef: React.RefCallback<HTMLDivElement>,
}

type NativeAnchorProps = Omit<ComponentProps<'a'>, 'onChange'>;

export interface TabMenuItem extends DropdownMenuItemProps {
    value: string,
}

export interface TabMenuGroup {
    /**
     * Group header caption displayed above the grouped items
     */
    caption: string,
    /**
     * Menu items belonging to this group
     */
    items: TabMenuItem[],
}

function isTabMenuGroup(item: TabMenuItem | TabMenuGroup): item is TabMenuGroup {
    return 'items' in item && Array.isArray(item.items);
}

export interface TabProps extends NativeAnchorProps {
    /**
     * Label rendered inside the tab button. May be a string or any `ReactNode`
     * (e.g. a span with an icon). Ellipsis truncation is controlled by the parent
     * `<Tabs>` `ellipsis` prop.
     */
    caption: ReactNode,
    /**
     * When `true` (default), clicking the tab fires selection and calls `onChange`.
     * Set to `false` when the tab should only open a dropdown (`menuItems`) without
     * directly selecting a value.
     */
    clickable?: boolean,
    /**
     * When `true`, the tab is non-interactive: focus is skipped, `aria-disabled` is
     * set, and click/keyboard events are suppressed.
     */
    disabled?: boolean,
    /**
     * Node rendered after the tab caption inside the anchor (e.g. a close icon, a
     * badge, or a `<TabBadge>`). Hidden from assistive technology via `aria-hidden`.
     */
    endAdornment?: ReactNode,
    /**
     * Called when this specific tab is selected, in addition to the parent `<Tabs>`
     * `onChange`. Receives the tab's `value` string.
     */
    onChange?: (value: string) => void,
    /**
     * DropdownMenu items. Supports flat items and grouped items.
     *
     * @example
     * ```tsx
     * menuItems={[
     *     { caption: 'Ungrouped Item', value: 'item1' },
     *     {
     *         caption: 'Group 1',
     *         items: [
     *             { caption: 'Item A', value: 'a' },
     *             { caption: 'Item B', value: 'b' },
     *         ],
     *     },
     * ]}
     * ```
     */
    menuItems?: (TabMenuItem | TabMenuGroup)[],
    /**
     * Node rendered before the tab caption inside the anchor (e.g. a `<TabIcon>`
     * or `<TabImage>`). Hidden from assistive technology via `aria-hidden`.
     */
    startAdornment?: ReactNode,
    /**
     * Props forwarded to the internal `<DropdownMenu>` when `menuItems` is
     * provided. Use to customize placement, maxHeight, or other dropdown options.
     */
    dropdownMenuProps?: Partial<ComponentProps<typeof DropdownMenu>>,
    /**
     * Render prop for fully custom tab content. When provided, replaces the
     * default anchor + caption + adornment structure entirely. Receives all
     * `TabProps` for use in the custom render.
     */
    renderTabContent?: (props: TabProps) => ReactNode,
    /**
     * Ref forwarded to the root `<li>` element.
     */
    rootRef?: React.Ref<HTMLLIElement>,
    /**
     * Node rendered after the tab content inside the root `<li>`, outside the
     * interactive anchor. Use for supplementary controls that should not be part
     * of the focusable tab element.
     */
    rootEndAdornment?: ReactNode,
    /**
     * Native props forwarded to the root `<li>` element (e.g. `className`, `style`,
     * `data-testid`).
     */
    rootProps?: ComponentProps<'li'>,
    /**
     * Unique string identifier for this tab. Used as `aria-selected` matching key
     * in the parent `<Tabs>` `value` state and as the argument to `onChange`.
     */
    value: string,
}

/**
 * The **Tab** component provides selectable and accessible navigation between sections of content.
 *
 * **Features**
 *
 * - Keyboard accessible (`ArrowLeft`, `ArrowRight`, `Tab`, `Shift + F10`, `Esc` keys etc.).
 * - Uses `aria-selected` to indicate the active tab.
 * - Supports `aria-controls` for linking tabs with their content.
 * - `aria-disabled` for non-interactive tabs.
 * - Dropdown support for tabs with multiple options.
 * - Customizable with `startAdornment` and `endAdornment` (icons, badges, etc.).
 *
 * **Migration from the old Tab component**
 *
 *   - Removed `iconBefore`, `iconAfter`, `image`, `iconBeforeSize`, `badge`. Now use `TabIcon`, `TabBadge`, or `TabImage` components inside `startAdornment` / `endAdornment`.
 *   - Removed `mix`, and `tabLinkMix`. Use `className` instead.
 *   - `name` renamed to `value` (`string`) and acts as the unique tab identifier.
 *   - Removed `data`. Use native attributes instead.
 *   - Removed `tabTooltip`. Use `anchorProps.title` instead.
 *   - Removed `title`. Use `dropdownMenuProps` to pass a title to `DropdownMenu`.
 *
 * **A11y usage notes**
 * - Tabs should have aria-label or aria-labelledby
 *
 * @see [ARIA Tabs Pattern](https://www.w3.org/WAI/ARIA/apg/patterns/tabs/)
 * @see [Storybook](https://storybook.dev.pitchbookdata.com/?path=/docs/components-tabs-tab--documentation)
 * @see [Changelog](https://gitlab.com/pitchbook/pb-core/platform-fe/ui-kit/-/blob/master/ts-packages/kit/components/tabs/CHANGELOG.md)
 * @see [GitLab](https://gitlab.com/pitchbook/pb-core/platform-fe/ui-kit/-/tree/master/ts-packages/kit/components/tabs)
 */

export const Tab = forwardRef<HTMLAnchorElement, TabProps>(
    function Tab(props, ref) {
        const {
            caption,
            clickable = true,
            disabled = false,
            dropdownMenuProps,
            endAdornment = null,
            onChange,
            menuItems = [],
            renderTabContent,
            rootEndAdornment,
            rootProps,
            rootRef: rootRefProp,
            startAdornment = null,
            value,
            ...anchorProps
        } = props;

        const [isDropdownMenuOpen, setDropdownMenuOpen] = useState<boolean>(false);

        const rootRef = React.useRef<HTMLLIElement>(null);
        useImperativeHandle(rootRefProp, () => rootRef.current as HTMLLIElement);

        const {
            ref: navigationRef,
            tabIndex,
            addSelf,
            removeSelf,
            setActive,
        } = useArrowNavigationItem();
        const {
            enableArrowNavigation,
            ellipsis,
            value: tabsValue = null,
            onChange: onChangeContext,
            tabPanelId,
            tabsId,
        } = useContext(TabsContext);

        const id = `${tabsId}-${value}`;
        const selected = !!value && tabsValue === value || menuItems?.some(item =>
            isTabMenuGroup(item)
                ? item.items.some(groupItem => groupItem.value === tabsValue)
                : item?.value === tabsValue,
        );
        const hasChildren = menuItems.length > 0;
        const computedTabIndex = enableArrowNavigation ? tabIndex : 0;

        useEffect(() => {
            if (disabled || !enableArrowNavigation) {
                removeSelf();
            } else {
                addSelf();
            }
        }, [disabled, enableArrowNavigation, addSelf, removeSelf]);

        const activateTab = () => {
            if (clickable || !hasChildren) {
                setActive();
                onChangeContext?.(value);
                onChange?.(value);
            }
        };

        const onKeyDown = (event: React.KeyboardEvent<HTMLAnchorElement>) => {
            if (disabled) {
                return;
            }

            if (event.key === 'Enter' || event.key === ' ' || event.shiftKey && event.key === 'F10') {
                event.preventDefault();
                activateTab();
                if (hasChildren) {
                    setDropdownMenuOpen(true);
                }
            }

            anchorProps.onKeyDown?.(event);
        };

        const onClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
            event.preventDefault();
            if (disabled) {
                return;
            }
            activateTab();
            anchorProps.onClick?.(event);
        };

        const renderAnchor = ({anchorRef, getAnchorProps, positionRef}: RenderAnchorProps) => {
            const dropdownMenuAnchorProps: React.AnchorHTMLAttributes<HTMLAnchorElement> =
                getAnchorProps ? getAnchorProps({onClick}) : {};
            const {
                id: dropdownId,
                'aria-controls': dropdownAriaControls,
                ...restDropdownMenuAnchorProps
            } = dropdownMenuAnchorProps;

            const mergedId = [id, dropdownId].filter(Boolean).join(' ') || undefined;
            const mergedAriaControls = selected
                ? [tabPanelId, dropdownAriaControls].filter(Boolean).join(' ')
                : dropdownAriaControls || undefined;

            return (
                <a
                    id={mergedId}
                    role="tab"
                    tabIndex={disabled ? -1 : computedTabIndex}
                    aria-disabled={disabled || !clickable}
                    aria-selected={selected}
                    aria-controls={mergedAriaControls}
                    {...anchorProps}
                    {...restDropdownMenuAnchorProps}
                    ref={mergeRefs<HTMLAnchorElement>(
                        ref,
                        anchorRef,
                        navigationRef as React.RefObject<HTMLAnchorElement>,
                    )}
                    className={classNames('tabs__link', anchorProps?.className, {
                        'tabs__link_with-adornment': endAdornment,
                        'tabs__link_no-ellipsis': !ellipsis,
                    })}
                    onKeyDown={onKeyDown}>
                    {startAdornment ? <div aria-hidden="true">{startAdornment}</div> : null}
                    <TabCaption>{caption}</TabCaption>
                    {endAdornment ? (
                        <div
                            ref={positionRef}
                            className={classNames('tabs__adornment')}
                            aria-hidden="true">
                            {endAdornment}
                        </div>
                    ) : null}
                </a>
            );
        };

        const defaultTabContent = () => {
            if (!hasChildren) {
                return renderAnchor({
                    anchorRef: noop,
                    getAnchorProps: allProps => allProps,
                    positionRef: noop,
                });
            }

            return (
                <DropdownMenu
                    {...dropdownMenuProps}
                    type="FIXED"
                    hoverable={true}
                    anchor={renderAnchor}
                    onKeyDown={(event: React.KeyboardEvent<HTMLDivElement>) => {
                        if (event.key === 'Escape' || event.key === 'Tab') {
                            event.preventDefault();
                            setDropdownMenuOpen(false);
                        }
                    }}
                    hoverProps={{
                        delay: {
                            open: 300,
                        },
                    }}
                    setShown={setDropdownMenuOpen}
                    shown={isDropdownMenuOpen}>
                    {menuItems?.map((item, index) => {
                        if (isTabMenuGroup(item)) {
                            return (
                                <DropdownMenuGroup key={`group-${index}`} caption={item.caption}>
                                    {item.items.map(
                                        ({value: menuItemValue, onClick: onMenuItemClick, ...menuItemProps}) => (
                                            <DropdownMenuItem
                                                key={menuItemValue}
                                                {...menuItemProps}
                                                onClick={e => {
                                                    onChangeContext?.(menuItemValue);
                                                    onMenuItemClick?.(e);
                                                }}
                                            />
                                        ))}
                                </DropdownMenuGroup>
                            );
                        }

                        const {value: menuItemValue, onClick: onMenuItemClick, ...menuItemProps} = item;

                        return (
                            <DropdownMenuItem
                                key={menuItemValue}
                                {...menuItemProps}
                                onClick={e => {
                                    onChangeContext?.(menuItemValue);
                                    onMenuItemClick?.(e);
                                }}
                            />
                        );
                    })}
                </DropdownMenu>
            );
        };

        const rootElementProps = {
            role: 'presentation',
            ...rootProps,
            ref: rootRef,
            disabled,
            className: classNames('tabs__item', rootProps?.className, {
                tabs__item_active: selected,
                tabs__item_disabled: disabled,
            }),
        };

        return (
            <li {...rootElementProps}>
                {renderTabContent ? renderTabContent(props) : defaultTabContent()}
                {rootEndAdornment}
            </li>
        );
    },
);

Tab.displayName = 'Tab';

export interface SidebarTabProps extends TabProps {
    /**
     * When `true`, applies the `tabs_sidebar__item_highlighted` modifier class to
     * visually distinguish this tab (e.g. as a currently active section).
     */
    isHighlighted?: boolean,
}

export const SidebarTab = ({isHighlighted, ...props}: SidebarTabProps) => {
    const sidebarClasses = classNames('tabs_sidebar__item', {
        tabs_sidebar__item_highlighted: isHighlighted,
    });

    return <Tab {...props} rootProps={{className: sidebarClasses}}/>;
};

SidebarTab.displayName = 'SidebarTab';

export const LinkTab = (props: TabProps) => (
    <Tab
        id={undefined}
        role={undefined}
        aria-selected={undefined}
        aria-disabled={undefined}
        aria-controls={undefined}
        rootProps={{role: undefined}}
        {...props}
    />
);

LinkTab.displayName = 'LinkTab';
